import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  isAuthenticated,
  PENDING_VERIFICATION_EMAIL_KEY,
} from "../utils/auth";
import {
  API_BASE_URL,
  FRONTEND_BASE_URL,
  GOOGLE_CLIENT_ID,
} from "../api/index";
import { useAnalytics } from "../hooks/useAnalytics";
import { setUserProperties, trackSignUp } from "../utils/analytics";
import {
  trackLoginFailure,
  trackLoginAttempt,
} from "../utils/mixpanel";

export const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState(
    searchParams.get("ref") || "",
  );
  const [referralInfo, setReferralInfo] = useState<any>(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize analytics
  const analytics = useAnalytics();

  const GOOGLE_REDIRECT_URI = encodeURIComponent(
    `${FRONTEND_BASE_URL}/oauth/callback`,
  );
  const GOOGLE_SCOPE = encodeURIComponent("openid email profile");

  useEffect(() => {
    if (!isAuthenticated()) return;
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  // Validate referral code when it changes
  useEffect(() => {
    if (referralCode && referralCode.length >= 6) {
      validateReferralCode(referralCode);
    } else {
      setReferralInfo(null);
    }
  }, [referralCode]);

  const validateReferralCode = async (code: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/referral/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralCode: code }),
      });

      const data = await response.json();
      if (data.success && data.valid) {
        setReferralInfo(data);
      } else {
        setReferralInfo(null);
      }
    } catch (_error) {
      setReferralInfo(null);
    }
  };

  const handleGoogleLogin = () => {
    // Track Google signup attempt
    analytics.trackEvent({
      action: "signup_attempt",
      category: "engagement",
      label: "google",
      customParameters: { method: "google" },
    });

    // Also track in Mixpanel
    try {
      trackLoginAttempt("google", { referral_code: referralCode });
    } catch (error) {
      console.error("Error tracking Google signup attempt:", error);
    }

    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("oauth_state", state);
    const url = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${GOOGLE_REDIRECT_URI}&scope=${GOOGLE_SCOPE}&access_type=offline&state=${state}`;

    window.location.href = url;
  };

  const handleEmailSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setStatus("");

    // Client-side validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          referralCode: referralCode || undefined,
        }),
      });
      const data = await res.json();

      if (data.success) {
        sessionStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, email);
        if (data.user) {
          localStorage.setItem("userData", JSON.stringify(data.user));
        }

        setUserProperties(data.user.userId);
        trackSignUp("email");

        setStatus("✅ Account created. Please verify your email.");
        navigate("/verify-email");
      } else {
        const errorMessage =
          data.error?.message || data.message || "Signup failed";

        // Track signup failure in Mixpanel
        try {
          trackLoginFailure("email", errorMessage, {
            email: email,
            referral_code: referralCode,
          });
        } catch (error) {
          console.error("Error tracking email signup failure:", error);
        }

        if (errorMessage.includes("already registered")) {
          setError(`${errorMessage} Click here to go to login instead.`);
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      const errorMessage = "Network error: " + (err as Error).message;

      // Track network error in Mixpanel
      try {
        import("../utils/mixpanel").then(({ trackLoginFailure }) => {
          trackLoginFailure("email", errorMessage, {
            email: email,
            referral_code: referralCode,
          });
        });
      } catch (error) {
        console.error("Error tracking network error:", error);
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{" "}
          <Link
            to="/login"
            className="font-medium text-indigo-600 hover:text-indigo-500 underline underline-offset-2"
          >
            sign in to your existing account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <button
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mb-4"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or sign up with email
              </span>
            </div>
          </div>

          <form className="mt-6 space-y-6" onSubmit={handleEmailSignup}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Create a password (min 8 characters)"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="referralCode"
                className="block text-sm font-medium text-gray-700"
              >
                Referral Code (Optional)
              </label>
              <div className="mt-1">
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={(e) =>
                    setReferralCode(e.target.value.toUpperCase())
                  }
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter referral code for bonus week"
                />
              </div>
              {referralInfo && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-green-400 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        🎉 Valid referral code from {referralInfo.referrer.name}
                        !
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {referralInfo.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {error.includes("already registered") ? (
                        <>
                          {error.split("Click here")[0]}
                          <Link
                            to="/login"
                            className="underline font-medium hover:text-red-900"
                          >
                            Click here to go to login instead.
                          </Link>
                        </>
                      ) : (
                        error
                      )}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            {status && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      {status}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating account...
                  </>
                ) : (
                  "Create account"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-xs text-gray-500">
            By creating an account, you agree to our{" "}
            <Link to="/terms" className="text-indigo-600 hover:text-indigo-500 underline underline-offset-2">
              Terms of Service
            </Link>
            {", "}
            <Link
              to="/privacy"
              className="text-indigo-600 hover:text-indigo-500 underline underline-offset-2"
            >
              Privacy Policy
            </Link>
            {", and "}
            <Link
              to="/refund"
              className="text-indigo-600 hover:text-indigo-500 underline underline-offset-2"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
